import DashboardReportingPlaceholder from "./DashboardReportingPlaceholder";
import { useMemo } from "react";

import DeliveryChart, {
  DeliveryChartLegend,
} from "../platformAnalytics/DeliveryChart";
import {
  buildTimeSeriesDeliveryChartModel,
  buildWindowTotalsDeliveryChartModel,
} from "../platformAnalytics/deliveryChartModel";
import {
  CLIENT_BILLED_REPORTING_BASIS,
  formatObservedReportingWindowLabel,
  formatReportingBasisLabel,
  resolveObservedReportingWindow,
} from "../platformAnalytics/observedReportingWindow";
import { indexObservedSiteVisitsByDate } from "../platformAnalytics/observedSiteVisits";
import { describeSpendUnavailable } from "../platformAnalytics/spendCoverageNotice";
import {
  buildCampaignDeliveryTooltipRows,
  resolveCampaignDelivery,
  resolvePostFlightDeliveryNotice,
} from "../platformAnalytics/campaignDeliveryAttribution";
import {
  aggregateEligibleRateBasis,
  readPlatformCounts,
  resolvePlatformEfficiencyMetric,
} from "../platformAnalytics/platformEfficiencyMetric";
/*
 * The canonical `cpx-` chart primitives live in the Platform Analytics page
 * stylesheet, which is the verbatim mirror of Quantum's. Every selector in it
 * is `.cpx-`-prefixed, so importing it here styles the chart and touches
 * nothing else on the Dashboard. It is imported by the component that renders
 * cpx- markup rather than by Dashboard.js, so the dependency travels with the
 * thing that needs it.
 */
import "../pages/ClientPlatformAnalytics.css";
import "./ClientPerformancePreview.css";
import { reportingTimeZoneStatement, responseReportingTimeZone, sourceCalendarQualification } from "../utils/reportingCalendar";

const PLATFORM_DEFINITIONS = [
  {
    key: "meta",
    label: "Meta",
    color: "#7c6cf0",
    channels: ["facebook", "instagram", "audience_network", "messenger", "meta"],
  },
  {
    key: "google_ads",
    label: "Google Ads",
    color: "#d96540",
    channels: ["google_ads", "google_search", "google"],
  },
  {
    key: "youtube",
    label: "YouTube",
    color: "#1e97cc",
    channels: ["youtube"],
  },
  {
    key: "tiktok",
    label: "TikTok",
    color: "#d8409f",
    channels: ["tiktok"],
  },
  {
    key: "ctv",
    label: "CTV",
    color: "#1da36f",
    channels: ["ctv", "connected_tv"],
  },
  {
    key: "programmatic",
    label: "Programmatic",
    color: "#4f7df0",
    channels: [
      "display",
      "video",
      "audio",
      "mobile",
      "ingame",
      "dooh",
      "programmatic",
    ],
  },
  {
    key: "amazon_ads",
    label: "Amazon Ads",
    color: "#b5820f",
    channels: ["amazon", "amazon_ads"],
  },
  // BR-2 (Phase 4 review, 2026-09-13): Yahoo! DSP and Viant are canonical reporting channels;
  // without a named entry their delivery folded into "Other". The branding registry mirrors
  // these hex values. OWNER DECISION: placeholder colours pending the categorical palette.
  { key: "yahoo_dsp", label: "Yahoo!", color: "#7b3fe4", channels: ["yahoo_dsp", "yahoo"] },
  { key: "viant", label: "Viant", color: "#c2410c", channels: ["viant", "viant_dsp", "adelphic"] },
  { key: "other", label: "Other", color: "#93a0b8", channels: [] },
];

const PLATFORM_BY_CHANNEL = new Map(
  PLATFORM_DEFINITIONS.flatMap((platform) =>
    platform.channels.map((channel) => [channel, platform])
  )
);
const AGGREGATE_PLATFORM = {
  key: "aggregate",
  label: "All platforms",
  color: "#53e7ff",
  channels: [],
};
const MEASUREMENT_ONLY_CHANNELS = new Set([
  "analytics",
  "ga",
  "ga4",
  "google_analytics",
]);

function toFiniteNumber(value) {
  if (value == null || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function normalizeChannelKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function formatCurrency(value) {
  if (!Number.isFinite(value)) return "Unavailable";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: value >= 100 ? 0 : 2,
  }).format(value);
}

function formatPercent(value) {
  if (!Number.isFinite(value)) return "Unavailable";
  return `${value.toFixed(2)}%`;
}

/* Axis ticks and column labels on a dashboard card have no room for
   "$1,234,567". The summary sentence below the chart still states the exact
   figure, so compacting here abbreviates the reading without losing it. */
function formatCompactCurrency(value) {
  if (!Number.isFinite(value)) return "Unavailable";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: value >= 10000 ? "compact" : "standard",
    maximumFractionDigits: value >= 10000 ? 1 : 0,
  }).format(value);
}

function formatCount(value) {
  if (!Number.isFinite(value)) return "Unavailable";
  return new Intl.NumberFormat("en-US", {
    notation: value >= 10000 ? "compact" : "standard",
    maximumFractionDigits: 1,
  }).format(value);
}

function formatDate(value) {
  const dateKey = String(value || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return "";
  const parsed = new Date(`${dateKey}T00:00:00Z`);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
  }).format(parsed);
}

/**
 * Governed Dashboard → Platform Analytics context handoff. The preview's
 * resolved reporting window (the one the user is looking at) is carried into
 * Platform Analytics as explicit governed date params so both surfaces
 * resolve the same canonical window. Only dates and a diagnostic origin
 * marker travel — never metrics or financial context. When the preview has
 * no resolved window the handoff degrades to the bare route.
 */
export function buildPlatformAnalyticsHandoffPath(canonicalSummary) {
  const isIsoDay = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));
  const timeframe = canonicalSummary?.timeframe || {};
  const start = String(
    timeframe.start || timeframe.startDate || timeframe.dateStart || ""
  ).slice(0, 10);
  const end = String(
    timeframe.end || timeframe.endDate || timeframe.dateEnd || ""
  ).slice(0, 10);
  if (!isIsoDay(start) || !isIsoDay(end) || start > end) {
    return "/platform-analytics";
  }
  const query = new URLSearchParams({
    startDate: start,
    endDate: end,
    origin: "dashboard_preview",
  });
  return `/platform-analytics?${query.toString()}`;
}

function resolvePlatformDefinition(value) {
  const normalized = normalizeChannelKey(value);
  if (!normalized) return PLATFORM_DEFINITIONS[PLATFORM_DEFINITIONS.length - 1];
  if (MEASUREMENT_ONLY_CHANNELS.has(normalized)) return null;
  const direct = PLATFORM_BY_CHANNEL.get(normalized);
  if (direct) return direct;
  if (normalized.includes("meta")) return PLATFORM_DEFINITIONS[0];
  if (normalized.includes("google")) return PLATFORM_DEFINITIONS[1];
  if (normalized.includes("youtube")) return PLATFORM_DEFINITIONS[2];
  if (normalized.includes("tiktok")) return PLATFORM_DEFINITIONS[3];
  if (normalized.includes("ctv") || normalized.includes("connected_tv")) {
    return PLATFORM_DEFINITIONS[4];
  }
  if (
    ["display", "video", "audio", "mobile", "ingame", "dooh"].some((token) =>
      normalized.includes(token)
    )
  ) {
    return PLATFORM_DEFINITIONS[5];
  }
  if (normalized.includes("amazon")) return PLATFORM_DEFINITIONS[6];
  return PLATFORM_DEFINITIONS[PLATFORM_DEFINITIONS.length - 1];
}

function addMetricsToPlatform(totals, platform, metrics) {
  if (!platform || !metrics || typeof metrics !== "object") return;
  const current = totals.get(platform.key) || {
    ...platform,
    spend: null,
    impressions: null,
    clicks: null,
    /*
     * Carried so a platform can be read on the metric that describes it.
     *
     * CTV reports zero clicks by design, and no generic video view either —
     * its reading is the COMPLETION rate, so the starts and completions it
     * divides have to reach the resolver. Without them the legend had the
     * platform but not the evidence, and CTV rendered with no statistic at
     * all beside it.
     */
    videoViews: null,
    videoStarts: null,
    videoCompletions: null,
    /* The source's own stated rate and how many channels claimed one — the
       governed precedence needs both, and it is the same precedence the
       focused KPI strip applies, so the two surfaces agree to the decimal. */
    completionRate: null,
    completionRateChannels: 0,
  };
  const add = (field, rawValue) => {
    const value = toFiniteNumber(rawValue);
    if (value == null) return;
    current[field] = (current[field] || 0) + value;
  };

  add(
    "spend",
    metrics.clientFacingSpend ??
      metrics.spendClientFacing ??
      metrics.billedSpend ??
      metrics.spend ??
      metrics.spendToDate
  );
  add("impressions", metrics.impressions);
  add("clicks", metrics.clicks);
  /* Canonical `byChannel` entries are snake_cased; the platform inventory and
     the report model are camelCased. Same observation, either spelling. */
  add("videoViews", metrics.videoViews ?? metrics.video_views);
  /* The video evidence, read through the byte-pinned reader that already
     knows every spelling the canonical layer and the welcome-sourced tier
     emit — rather than a second alias table maintained beside it. */
  const counts = readPlatformCounts(metrics);
  add("videoStarts", counts.videoStarts);
  add("videoCompletions", counts.videoCompletions);
  /* A rate is not additive, so it is not summed: the last one stated is kept
     alongside a count of how many channels stated one, and the resolver
     discards it the moment that count exceeds one. */
  if (counts.completionRate != null) {
    current.completionRate = counts.completionRate;
    current.completionRateChannels += 1;
  }
  totals.set(platform.key, current);
}

/*
 * The canonical summary describes the SAME delivery in more than one shape:
 * `byChannel` carries the canonical per-channel metrics, and `platforms` /
 * `platformBreakdown` / `byPlatform` are alternate projections of that same
 * data (the backend platform inventory mirrors `clientFacingSpend` from
 * `byChannel`). The shapes exist for shape tolerance across payload
 * versions — they are never additive sources. Summing more than one counts
 * the same client-billed dollar once per shape, so aggregation reads the
 * first shape that yields a delivery signal and stops there.
 */
function aggregatePlatformRowsFromEntries(entries) {
  const totals = new Map();
  entries.forEach(({ definition, metrics }) => {
    addMetricsToPlatform(totals, definition, metrics);
  });
  return PLATFORM_DEFINITIONS.map((platform) => totals.get(platform.key)).filter(
    (platform) =>
      platform &&
      [platform.spend, platform.impressions, platform.clicks].some(
        (value) => Number.isFinite(value) && value > 0
      )
  );
}

function buildPlatformRows(summary, advertiserScopedMetrics = null) {
  const byChannel =
    summary?.byChannel && typeof summary.byChannel === "object"
      ? summary.byChannel
      : {};

  const shapeSources = [
    Object.entries(byChannel).map(([channelKey, metrics]) => ({
      definition: resolvePlatformDefinition(channelKey),
      metrics,
    })),
  ];

  const platformCandidates = [
    summary?.platformRows,
    summary?.platforms,
    summary?.platformBreakdown,
    summary?.byPlatform,
    summary?.metricsByPlatform,
  ].filter(Boolean);

  platformCandidates.forEach((candidate) => {
    const rows = Array.isArray(candidate)
      ? candidate
      : Object.entries(candidate).map(([key, value]) => ({
          ...(value && typeof value === "object" ? value : {}),
          platform: value?.platform || value?.label || key,
        }));

    shapeSources.push(
      rows.map((row) => ({
        definition: resolvePlatformDefinition(
          row?.platform ||
            row?.platformLabel ||
            row?.channel ||
            row?.label ||
            row?.name ||
            row?.key
        ),
        /* Platform-inventory rows nest their counts under `.metrics`; the
           flatten lets a row answer from either level without being read
           twice. Top-level fields win on conflict. */
        metrics:
          row?.metrics && typeof row.metrics === "object"
            ? { ...row.metrics, ...row }
            : row,
      }))
    );
  });

  for (const entries of shapeSources) {
    const platformRows = aggregatePlatformRowsFromEntries(entries);
    if (platformRows.length) return platformRows;
  }

  const canonicalAggregateMetrics =
    summary?.aggregateTotals || summary?.metrics || null;
  const aggregateMetrics = hasPositiveReportingSignal(canonicalAggregateMetrics)
    ? canonicalAggregateMetrics
    : advertiserScopedMetrics;
  const aggregateTotals = new Map();
  addMetricsToPlatform(aggregateTotals, AGGREGATE_PLATFORM, aggregateMetrics);
  const aggregate = aggregateTotals.get(AGGREGATE_PLATFORM.key);
  return aggregate &&
    [aggregate.spend, aggregate.impressions, aggregate.clicks].some(
      (value) => Number.isFinite(value) && value > 0
    )
    ? [aggregate]
    : [];
}

function hasPositiveReportingSignal(metrics) {
  return (
    metrics &&
    typeof metrics === "object" &&
    [
      metrics.clientFacingSpend,
      metrics.spendClientFacing,
      metrics.billedSpend,
      metrics.spend,
      metrics.spendToDate,
      metrics.impressions,
      metrics.clicks,
    ].some((value) => {
      const numeric = toFiniteNumber(value);
      return numeric != null && numeric > 0;
    })
  );
}

export function buildClientPerformancePreviewModel({
  canonicalSummary = null,
  advertiserScopedMetrics = null,
  loading = false,
  error = null,
  coverageLabel = "",
  campaignsRunning = null,
  campaignWindowState = null,
} = {}) {
  if (loading) {
    return {
      state: "loading",
      message: "Loading cross-platform reporting.",
    };
  }

  if (error) {
    return {
      state: "error",
      message:
        "Performance reporting is temporarily unavailable. The rest of the dashboard remains available.",
    };
  }

  const platforms = buildPlatformRows(
    canonicalSummary,
    advertiserScopedMetrics
  );
  if (!platforms.length) {
    if (["missing_dates", "planned"].includes(campaignWindowState)) {
      return {
        state: "window_pending",
        message:
          "Campaign flight window pending. Performance reporting will appear when the reporting window is available.",
      };
    }
    if (campaignsRunning != null && Number(campaignsRunning) === 0) {
      return {
        state: "no_campaign",
        message: "No active campaign reporting is available for this period.",
      };
    }
    return {
      state: "no_data",
      message:
        "Cross-platform delivery will appear as reporting data becomes available.",
    };
  }

  const spendValues = platforms
    .map((platform) => platform.spend)
    .filter(Number.isFinite);
  const totalSpend = spendValues.length
    ? spendValues.reduce((sum, value) => sum + value, 0)
    : null;
  /*
   * Missing is never zero. A platform that delivered in this window without a
   * client-billed spend figure cannot be in the total, so the card says which
   * one is left out instead of letting the total read as the whole. (With no
   * total at all, the spend sentence already says so.) Quantum's welcome card
   * states the same sentence.
   */
  const spendUnavailablePlatforms = platforms
    .filter((platform) => !Number.isFinite(platform.spend))
    .map((platform) => platform.label);
  const spendUnavailableNotice = Number.isFinite(totalSpend)
    ? describeSpendUnavailable(spendUnavailablePlatforms)
    : "";
  /*
   * The card's aggregate CTR is scoped by the governed participation
   * resolver: summed clicks over summed impressions across the
   * CLICK-ELIGIBLE platforms only. CTV impressions never enter this
   * denominator — that inventory has no click to offer, and blending it in
   * is what used to print 0.05% across a mix whose click inventory was
   * actually clearing 0.08%. (An unattributed payload folds to the single
   * aggregate pseudo-platform, which is read as click inventory — with no
   * per-platform evidence there is no narrower honest scope to claim.)
   */
  const rateBasis = aggregateEligibleRateBasis(
    platforms.map((platform) => ({
      platformKey: platform.key,
      ...readPlatformCounts(platform),
    }))
  );
  const ctr = rateBasis.ctr.value;
  /*
   * The dated delivery this card actually DRAWS, resolved once here and handed
   * to the chart adapter below — one reading of the certified daily series, so
   * the columns and the window printed above them come from the same list of
   * days. Null when the payload carries no dated evidence, which is the signal
   * to name the canonical window the totals genuinely cover.
   */
  const dailyBuckets = buildPreviewDailyBuckets(canonicalSummary);
  const reportingWindow = resolveObservedReportingWindow({
    observedDates: dailyBuckets
      ? dailyBuckets.perDay.map((day) => day.date)
      : null,
    canonicalTimeframe: canonicalSummary?.timeframe || null,
  });
  const windowLabel = formatObservedReportingWindowLabel(reportingWindow);
  const hasPlatformAttribution = platforms.some(
    (platform) => platform.key !== AGGREGATE_PLATFORM.key
  );
  /* The count the prose states is the count the chart draws: platforms with
     positive client-billed spend — the rendered stack. A click-eligible row
     that reported signal but no billed delivery still informs the scoped CTR
     above, but "$X across N platforms" describes where the dollars went, and
     a $0 row is not one of those places. Quantum's welcome prose counts the
     same way. */
  const stackedPlatformCount = platforms.filter(
    (platform) => Number.isFinite(platform.spend) && platform.spend > 0
  ).length;
  const summary = [
    `Window totals for ${windowLabel}.`,
    Number.isFinite(totalSpend)
      ? hasPlatformAttribution
        ? `${formatCurrency(totalSpend)} in client-billed spend across ${
            stackedPlatformCount
          } platform${stackedPlatformCount === 1 ? "" : "s"}.`
        : `${formatCurrency(
            totalSpend
          )} in advertiser-scoped client-billed spend; platform attribution is unavailable for this preview.`
      : "Spend is unavailable for this reporting window.",
    spendUnavailableNotice || null,
    Number.isFinite(ctr)
      ? `CTR across click-reporting platforms is ${formatPercent(ctr)}.`
      : "CTR is unavailable because no click-reporting platform has clicks and impressions.",
  ]
    .filter(Boolean)
    .join(" ");

  const timeframe = canonicalSummary?.timeframe || {};
  const startDate = String(
    timeframe.start || timeframe.startDate || timeframe.dateStart || ""
  ).slice(0, 10);
  const endDate = String(
    timeframe.end || timeframe.endDate || timeframe.dateEnd || ""
  ).slice(0, 10);
  /* The date that keys the window-total bucket — Quantum's welcome adapter
     resolves it the same way: the reporting window's end date, falling back to
     the payload's generation date. Without either there is no date to claim,
     and the chart stays on the platform axis. */
  const isIsoDay = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value);
  const generatedAt = String(
    canonicalSummary?.dataFreshness?.generatedAt || ""
  ).slice(0, 10);
  const windowBucketDate = isIsoDay(endDate)
    ? endDate
    : isIsoDay(generatedAt)
      ? generatedAt
      : null;

  return {
    state: "ready",
    representation: dailyBuckets ? "daily" : "aggregate",
    platforms,
    dailyBuckets,
    totalSpend,
    spendUnavailablePlatforms,
    spendUnavailableNotice,
    ctr,
    observedReportingWindow: reportingWindow,
    windowLabel,
    // The calendar the canonical answer states its days in (null if none).
    reportingTimeZone: responseReportingTimeZone(canonicalSummary),
    // Which platforms report on their own account calendars, or whose zone
    // was not captured — said beside the calendar, never left implied.
    sourceCalendarNote: sourceCalendarQualification(canonicalSummary),
    /* This preview reads the canonical client-billed summary and nothing else,
       so it declares that basis rather than inferring one — but it declares it
       as a KEY and lets the shared formatter write the copy, which is what
       keeps this caption and Quantum's the same sentence. */
    basisLabel: formatReportingBasisLabel(CLIENT_BILLED_REPORTING_BASIS),
    /* The canonical chart names its window in its own text summary. */
    reportingWindow: startDate && endDate ? { startDate, endDate } : null,
    windowBucketDate,
    summary: coverageLabel ? `${coverageLabel}. ${summary}` : summary,
  };
}

/*
 * The welcome panel's reading of the canonical Delivery chart.
 *
 * This used to be a bespoke horizontal bar with a decorative CTR rule that
 * meant nothing — a second picture of the same delivery, free to drift away
 * from the one Platform Analytics draws. It is now the SAME component at
 * dashboard-card size, fed the same chart state Quantum's welcome panel
 * renders for the same payload.
 *
 * The welcome payload is window totals only (canonical_reporting_v1 carries no
 * certified daily series). Quantum's welcome adapter wraps those totals into
 * ONE bucket keyed by the reporting window's end date — a single stacked
 * column on the time axis, with the platforms stacked inside it. The chart's
 * own date axis and text summary are what state that reading; no banner
 * restates it. This adapter mirrors that treatment through the SHARED
 * time-series model builder, so the two welcome panels draw the same picture
 * from the same totals, and when the governed daily contract certifies, the
 * same builder graduates to many dated buckets without a redesign.
 *
 * Nothing here fabricates a date: the bucket's date is the canonical window's
 * own end date (never a day the window does not cover, never an invented
 * future day), and a payload that carries no resolvable date at all keeps the
 * platform-axis window-totals state rather than inventing one.
 */
const PREVIEW_METRIC = Object.freeze({
  key: "spend",
  label: "Client-billed spend",
  kind: "currency",
});
const PREVIEW_CHART_METRICS = Object.freeze({
  spend: { key: "spend", label: PREVIEW_METRIC.label, unit: "currency" },
  ctr: {
    key: "ctr",
    label: "CTR",
    unit: "percent",
    definition:
      "Clicks divided by impressions for the reporting window.",
  },
});

/*
 * `options` is the renderer's, not this adapter's: DeliveryChart asks for the
 * compact form on axis ticks, where a full number would not fit, and for
 * `{ compact: false }` in the tooltip — the one place on the card with room for
 * the exact figure. Dropping the options is what made an observation of 1,716
 * sessions read "1.7K" in the very place a reader goes to find out what it was.
 *
 * Counts only. Currency stays compact wherever it already was: the card's spend
 * presentation is the approved one and is not what the overlay is here to
 * change. Platform Analytics' daily adapter draws the same line in the same
 * place.
 */
function formatPreviewMetricValue(unit, value, options = {}) {
  if (unit === "percent") return formatPercent(value);
  if (unit === "currency") return formatCompactCurrency(value);
  if (options.compact === false && Number.isFinite(value)) {
    return value.toLocaleString("en-US", { maximumFractionDigits: 0 });
  }
  return formatCount(value);
}

/**
 * The platform's own efficiency reading, formatted.
 *
 * Governed per platform, not assumed to be CTR. CTV is bought on completed
 * viewing and reports zero clicks, so a CTR reading on it is a true, reported,
 * meaningless 0.00% — and the card used to print the whole card's BLENDED CTR
 * (0.05% across CTV and Display together), which is a rate over two
 * populations that are not read on the same metric. Each platform states its
 * own rate now, or none, and nothing is blended.
 */
function resolvePreviewRate(row) {
  const resolved = resolvePlatformEfficiencyMetric({
    platformKey: row?.key,
    ...readPlatformCounts(row),
  });
  return resolved
    ? { label: resolved.label, value: formatPercent(resolved.value) }
    : null;
}

/**
 * The governed daily series → dated buckets, one per reporting day.
 *
 * Returns null when the payload carries no dated evidence, which is the signal
 * to fall back to the single window-total bucket below.
 */
function buildPreviewDailyBuckets(canonicalSummary) {
  const daily = canonicalSummary?.dailyByChannel;
  const days = Array.isArray(daily?.days) ? daily.days : [];
  if (!days.length) return null;

  const perDay = days
    .map((day) => {
      const totals = new Map();
      Object.entries(day?.byChannel || {}).forEach(([channelKey, metrics]) => {
        addMetricsToPlatform(totals, resolvePlatformDefinition(channelKey), metrics);
      });
      return { date: String(day?.date || "").slice(0, 10), totals };
    })
    .filter((day) => /^\d{4}-\d{2}-\d{2}$/.test(day.date) && day.totals.size > 0);

  if (!perDay.length) return null;
  return { perDay, complete: daily?.complete !== false };
}

/**
 * The observed GA4 series the canonical payload carries, keyed by its own date.
 *
 * This is the SAME governed series Platform Analytics overlays and the public
 * report publishes — read off the canonical summary the welcome panel was
 * already given. The card never requests GA4 itself: a second fetch would
 * resolve its own window and its own failure story, and the overlay would start
 * describing a period the columns beneath it do not.
 *
 * A reported 0 is an observation and stays. A day absent from the payload stays
 * absent, which is what leaves a hole in the overlay instead of a floor that
 * never happened.
 */
function buildPreviewSiteVisits(canonicalSummary) {
  /*
   * Located by the shared resolver, not by a property read of its own. The card
   * is normally handed an unwrapped canonical summary, but "which envelope was
   * this?" is not a question each surface gets to answer separately — one
   * adapter answering it differently is exactly how Platform Analytics came to
   * draw the same payload's delivery with no overlay at all.
   */
  return indexObservedSiteVisitsByDate(canonicalSummary);
}

/**
 * What the overlay adds to the card's spoken description.
 *
 * The card prints no summary sentence — this travels in the chart's aria-label
 * — so for a reader who cannot see the gap in the amber line, this is the only
 * place that says which days were observed and that the rest are unreported
 * rather than zero.
 *
 * It opens with the series' OWN governed name rather than a fixed one. A
 * campaign whose outcome semantics were verified carries attributed visits, not
 * advertiser-wide sessions, and the two differ in what may be claimed about
 * them — so the correlation-only disclaimer is swapped for the attribution it
 * actually rests on instead of being recited over a series it does not describe.
 */
function buildSiteVisitsTextSummary({ buckets, timeZone, label, metric }) {
  const plotted = buckets.filter((bucket) => bucket.siteVisits != null);
  if (!plotted.length) return "";
  const resolvedLabel = label || "Site Visits · GA4";
  const attributed = metric === "ad_attributed_site_visits";
  return [
    ` ${resolvedLabel}${
      timeZone ? `, dates in ${timeZone}` : ""
    } is overlaid on a secondary right-hand axis with its own scale, not on the spend scale: `,
    plotted
      .map(
        (bucket) =>
          // Spoken in full: "1.7K" is a glance format, and a description read
          // aloud is where the exact observation belongs.
          `${bucket.label} ${bucket.siteVisits.toLocaleString("en-US", {
            maximumFractionDigits: 0,
          })}`
      )
      .join(", "),
    `. ${plotted.length} of ${buckets.length} reporting day${
      buckets.length === 1 ? "" : "s"
    } carr${plotted.length === 1 ? "ies" : "y"} ${
      attributed ? "an attributed observation" : "a GA4 observation"
    }; `,
    "any other day is unreported rather than zero, and the line breaks there instead of being drawn through it.",
    attributed
      ? " Attributed by the delivery platform through a verified site-wide conversion tracker, " +
        "including both click-through and view-through attribution. View-through attribution may " +
        "overlap with other platforms' attribution models, and this counts attributed events " +
        "rather than unique visitors."
      : " Correlation only: this states traffic observed beside delivery and does not claim the media caused it.",
  ].join("");
}

export function buildPreviewDeliveryChartModel(model, canonicalSummary = null) {
  if (model.state !== "ready") return null;
  const platformsByKey = new Map(
    model.platforms.map((platform) => [platform.key, platform])
  );
  /* The preview's rows already carry the canonical categorical hexes, so
     identity is resolved from the rows themselves rather than through a
     second registry that could disagree with them. */
  const resolvePreviewPlatform = (key) => platformsByKey.get(key) || null;
  /*
   * Stack-presence rule (the welcome-panel chart contract, pinned by the
   * mirrored parity tests in both repos): the time-axis chart's platform set
   * states only platforms that are actually IN the rendered stack — positive
   * client-billed spend for the selected window. A spend-less row (a platform
   * that reported clicks but no billed delivery, an empty "Other" fold) is not
   * a series in this chart, so it may not claim a legend swatch. Delivery is
   * still never silently dropped: any platform that DOES carry spend appears,
   * in the stack and the legend, "Other" included.
   */
  const stackedPlatforms = model.platforms.filter(
    (platform) => Number.isFinite(platform.spend) && platform.spend > 0
  );
  const platformKeys = stackedPlatforms.map((platform) => platform.key);

  /* Each platform's own efficiency reading for the window, stated once, on its
     legend entry — where a reader sees it without hovering the chart. */
  const platformRates = Object.fromEntries(
    platformKeys
      .map((key) => {
        const rate = resolvePreviewRate(platformsByKey.get(key));
        return rate ? [key, rate] : null;
      })
      .filter(Boolean)
  );

  /*
   * The observed GA4 series, attached to the DELIVERY days this card already
   * draws, matched on the canonical date string both series are keyed by. A day
   * GA4 reported and media did not gets no column of its own: inventing an
   * empty delivery column to host a traffic point would state a reporting day
   * that delivery never had.
   */
  const siteVisits = buildPreviewSiteVisits(canonicalSummary);
  const siteVisitsFor = (dateKey) =>
    siteVisits.byDate.has(String(dateKey)) ? siteVisits.byDate.get(String(dateKey)) : null;

  const buildFromBuckets = (buckets, textSummary) =>
    buildTimeSeriesDeliveryChartModel({
      viewModel: {
        volumeMetricKey: "spend",
        efficiencyMetricKey: "ctr",
        /*
         * No chart-wide efficiency series. The card's platforms are read on
         * DIFFERENT metrics — CTR on click inventory, video view rate on video
         * inventory — so one series across them would blend definitions that
         * share no unit, and a single blended CTR is exactly the number that
         * made CTV look like a 0.00% campaign. The per-platform readings ride
         * the legend instead, which is also why nothing states a rate twice.
         */
        efficiencyComparable: false,
        platformKeys,
        platformRates,
        buckets,
        /*
         * Client vocabulary only. This hardcoded the contract's INTERNAL
         * basis label, which the chart then lower-cased into the legend as
         * "Plan (gross · client-billed)". The amount is the client's; the
         * word gross is not.
         */
        spendBasisClientLabel: "Client-billed",
        /*
         * Derived from the buckets that actually carry an observation, never
         * asserted: the flag drives the legend entry and the right-hand axis,
         * so declaring it on a payload with no plotted GA4 day would name a
         * series the chart cannot draw.
         *
         * GA4 is a measurement overlay, not a media platform. It rides its own
         * scale and its own legend entry, and it is nowhere in `platformKeys` —
         * so it cannot enter the stack, the spend total, the platform count, or
         * either rate's denominator.
         */
        siteVisits: {
          available: buckets.some((bucket) => bucket.siteVisits != null),
          /*
           * The name the SERIES declares, not one this card chooses. The
           * resolver hands back "Ad-Attributed Site Visits" for a verified
           * campaign-attributed series and the advertiser-wide GA4 name for
           * everything else, so the legend states the governed outcome rather
           * than its provenance — and the two portals, reading one payload
           * through one resolver, cannot end up naming it differently.
           */
          label: siteVisits.label || "Site Visits · GA4",
        },
        chartTextSummary: `${textSummary}${buildSiteVisitsTextSummary({
          buckets,
          timeZone: siteVisits.timeZone,
          label: siteVisits.label,
          metric: siteVisits.metric,
        })}`,
      },
      resolvePlatform: resolvePreviewPlatform,
      resolveMetric: (key) => PREVIEW_CHART_METRICS[key] || null,
      formatMetricValue: (unit, value, options) =>
        formatPreviewMetricValue(unit, value, options),
    });

  /*
   * PREFERRED: a column per reporting DAY, platforms stacked inside it. This is
   * the canonical delivery reading, and it is drawn from dated evidence only —
   * days nobody reported are absent, never zero, and the flight's remaining
   * days are not drawn at all.
   */
  const daily = model.dailyBuckets;
  if (daily) {
    /*
     * Campaign attribution rides each day's tooltip as governed extra rows:
     * which campaign a day's dollars belong to, on which platform, and
     * whether that day sat inside the campaign's approved flight. Read from
     * the canonical contract beside the daily series, so the rows reconcile
     * to the stacked total by construction. Absent when there is nothing to
     * reconcile (one campaign, all in flight).
     */
    const campaignDelivery = resolveCampaignDelivery(canonicalSummary);
    return buildFromBuckets(
      daily.perDay.map((day) => ({
        key: day.date,
        label: formatDate(day.date),
        byPlatform: Object.fromEntries(
          platformKeys
            .map((key) => {
              const row = day.totals.get(key);
              return row && Number.isFinite(row.spend) && row.spend > 0
                ? [key, { spend: row.spend }]
                : null;
            })
            .filter(Boolean)
        ),
        planMedia: 0,
        siteVisits: siteVisitsFor(day.date),
        extraRows: buildCampaignDeliveryTooltipRows({
          campaignDelivery,
          date: day.date,
          resolvePlatformLabel: (channelKey) =>
            resolvePlatformDefinition(channelKey)?.label || channelKey,
        }),
      })),
      buildDailyChartTextSummary(daily, model)
    );
  }

  /*
   * FALLBACK: window totals with no daily breakdown, wrapped into ONE bucket
   * keyed by the window's end date — the same treatment Quantum's welcome panel
   * gives the same payload. Nothing here fabricates a date: the bucket's date
   * is the canonical window's own end date, and a payload with no resolvable
   * date at all keeps the platform-axis state rather than inventing one.
   */
  if (model.windowBucketDate && stackedPlatforms.length) {
    return buildFromBuckets(
      [
        {
          key: model.windowBucketDate,
          label: formatDate(model.windowBucketDate),
          byPlatform: Object.fromEntries(
            stackedPlatforms.map((platform) => [
              platform.key,
              { spend: platform.spend },
            ])
          ),
          planMedia: 0,
          siteVisits: siteVisitsFor(model.windowBucketDate),
        },
      ],
      model.summary
    );
  }

  const windowTotals = buildWindowTotalsDeliveryChartModel({
    platforms: model.platforms,
    metric: PREVIEW_METRIC,
    resolveBranding: resolvePreviewPlatform,
    formatValue: (value, kind) =>
      kind === "currency" ? formatCompactCurrency(value) : formatCount(value),
    window: model.reportingWindow,
    resolveRate: resolvePreviewRate,
  });
  /* The undated fallback carries the card's summary too. The heading names
     the basis alone, so this summary is where coverage and freshness status
     (e.g. "Partial coverage · Previously loaded") reaches assistive tech —
     the dated paths above already pass model.summary to buildFromBuckets. */
  return {
    ...windowTotals,
    textSummary: [windowTotals.textSummary, model.summary]
      .filter(Boolean)
      .join(" "),
  };
}

function buildDailyChartTextSummary(daily, model) {
  const dayCount = daily.perDay.length;
  const first = formatDate(daily.perDay[0].date);
  const last = formatDate(daily.perDay[dayCount - 1].date);
  const span = dayCount === 1 ? first : `${first}–${last}`;
  return [
    `Client-billed spend by reporting day for ${span}, stacked by platform: `,
    daily.perDay
      .map((day) => {
        const parts = model.platforms
          .map((platform) => {
            const row = day.totals.get(platform.key);
            return row && row.spend > 0
              ? `${row.label} ${formatCompactCurrency(row.spend)}`
              : null;
          })
          .filter(Boolean);
        return `${formatDate(day.date)} — ${parts.join(", ")}`;
      })
      .join("; "),
    `. ${dayCount} reported day${dayCount === 1 ? "" : "s"}.`,
    " Days without reported delivery are left out rather than drawn as zero.",
    daily.complete
      ? ""
      : " Some delivery in this window was reported only as a window total and cannot be attributed to a day, so it is absent from these columns.",
    ` ${model.summary}`,
  ].join("");
}

export default function ClientPerformancePreview({
  canonicalSummary = null,
  advertiserScopedMetrics = null,
  loading = false,
  error = null,
  coverageLabel = "",
  campaignsRunning = null,
  campaignWindowState = null,
  onOpen,
}) {
  const model = useMemo(
    () =>
      buildClientPerformancePreviewModel({
        canonicalSummary,
        advertiserScopedMetrics,
        loading,
        error,
        coverageLabel,
        campaignsRunning,
        campaignWindowState,
      }),
    [
      canonicalSummary,
      advertiserScopedMetrics,
      loading,
      error,
      coverageLabel,
      campaignsRunning,
      campaignWindowState,
    ]
  );

  const deliveryChartModel = useMemo(
    () => buildPreviewDeliveryChartModel(model, canonicalSummary),
    [model, canonicalSummary]
  );
  /*
   * DELIVERY OUTSIDE APPROVED FLIGHT — stated from the canonical attribution
   * contract, never inferred from dates on this card. Null when every
   * campaign delivered inside its approved flight.
   */
  const postFlightNotice = useMemo(
    () =>
      resolvePostFlightDeliveryNotice({
        campaignDelivery: resolveCampaignDelivery(canonicalSummary),
        formatDate,
        resolvePlatformLabel: (channelKey) =>
          resolvePlatformDefinition(channelKey)?.label || channelKey,
      }),
    [canonicalSummary]
  );

  return (
    <section
      className="client-performance-preview"
      aria-label="Platform Performance"
      data-preview-state={model.state}
      data-preview-representation={model.representation || "none"}
    >
      <div className="client-performance-header">
        <div>
          <h3>Platform Performance</h3>
          <p>Spend and efficiency across active platforms</p>
        </div>
        <button
          type="button"
          className="client-performance-open"
          onClick={onOpen}
          aria-label="View Platform Analytics"
        >
          View Platform Analytics
        </button>
      </div>

      {model.state === "ready" ? (
        <>
          <div className="client-performance-meta">
            {/* The heading names the reporting basis and nothing else.
                Coverage and freshness stay in the model and the chart's
                accessible summary; they are never appended to this label. */}
            <span className="client-performance-meta-label">
              {model.basisLabel}
            </span>
            <span
              className="client-performance-meta-window cpx-numeric"
              data-reporting-time-zone={model.reportingTimeZone || undefined}
              title={[reportingTimeZoneStatement(model.reportingTimeZone), model.sourceCalendarNote].filter(Boolean).join(" · ") || undefined}
            >
              {model.windowLabel}
            </span>
          </div>
          {postFlightNotice ? (
            <p
              className="client-performance-flight-notice"
              role="note"
              data-post-flight-delivery="true"
            >
              <span className="client-performance-flight-notice-title">
                {postFlightNotice.title}
              </span>
              <span className="client-performance-flight-notice-detail">
                {postFlightNotice.detail}
              </span>
            </p>
          ) : null}
          {/* An integrity warning: a delivering platform's spend is missing
              from the total. Quantum states the same sentence. */}
          {model.spendUnavailableNotice ? (
            <p
              className="client-performance-state-note client-performance-state-note--warn"
              role="note"
              data-spend-unavailable="true"
            >
              {model.spendUnavailableNotice}
            </p>
          ) : null}
          {/* No reporting-mode banner here — the chart's own date axis, legend
              and text summary state what the picture is (including, in the
              dated state, how many days reported and that unattributable
              window-total delivery is absent from the columns). The rows a
              dashed notice used to occupy belong to the plot. */}
          <div
            className="client-performance-chart-shell"
            aria-label="Delivery and efficiency chart"
          >
            <DeliveryChart
              model={deliveryChartModel}
              mode="efficiency"
              density="mini"
            />
          </div>
          <div className="client-performance-legend" aria-label="Platform legend">
            <DeliveryChartLegend model={deliveryChartModel} mode="efficiency" />
          </div>
          {/* The card ends at the legend. model.summary is not printed here —
              every fact in it is already on the card (window on the reporting
              row and date axis, spend and platform count in the stack, each
              platform's own rate on its legend entry) — but it still travels
              to assistive technology as the canonical chart's text summary,
              inside the svg's aria-label. Quantum ends its card the same way;
              the mirrored parity suites pin the absence on both portals. */}
        </>
      ) : (
        <DashboardReportingPlaceholder loading={loading} />
      )}
    </section>
  );
}
